import {
  AfterViewInit,
  ApplicationRef,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  Injector,
  OnInit,
  Renderer2,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NavigateService } from '@shared/services/navigate.service';
import { FullCalendarComponent } from '@fullcalendar/angular';
import { LanguageService } from '@core/services/language.service';
import { ComponentPortal, DomPortalHost } from '@angular/cdk/portal';
import {
  PROJECT_STAGES_FILTER,
  projectsTimelineCalendarConfig
} from '@modules/projects/modules/projects-timeline/pages/projects-timeline/projects-timeline-calendar.config';
import { ProjectStageAPIService } from '@modules/projects/shared/services/project-stage-api.service';
import { IProjectStage } from '@modules/projects/shared/interfaces/project-stage.interface';
import { SnackBarService } from '@core/services/snackbar.service';
import { Subscription } from 'rxjs';
import { ProjectsTimelineResourceLabelComponent } from '@modules/projects/modules/projects-timeline/pages/projects-timeline/components/projects-timeline-resource-label/projects-timeline-resource-label.component';
import { IProjectsTimelineResourceEvent } from '@modules/projects/modules/projects-timeline/shared/interfaces/projects-timeline-resource-event.interface';
import * as moment from 'moment';
import { Config } from '@shared/configs/config';
import { ProjectAPIService } from '@modules/projects/shared/services/project-api.service';
import { Project } from '@modules/projects/shared/models/project.model';
import { ProjectStage } from '@shared/enums/project-stage.enum';
import { Duration, EventApi, ViewApi } from '@fullcalendar/core';
import { ProjectsTimelineEventController } from './controllers/projects-timeline-event.controller';
import { ProjectsTimelineResourceController } from './controllers/projects-timeline-resource.controller';
import { WindowHelper } from '@shared/helpers/window.helper';
import { debounce } from '@shared/decorators/debounce.decorator';
import { ProjectController } from '@modules/projects/shared/controllers/project.controller';
import {
  ProjectChangeStageEvent,
  ProjectChangeStageEventType
} from '@modules/projects/shared/components/project-lost-stage-modal/project-lost-stage-modal.component';
import { ProjectsTimelineStorageService } from '../../shared/services/projects-timeline-storage.service';
import { ProjectsTimelineHeaderComponent } from '@modules/projects/modules/projects-timeline/pages/projects-timeline/components/projects-timeline-header/projects-timeline-header.component';
import { SidenavEventType, SidenavService } from '@shared/services/sidenav/sidenav.service';
import { CalendarViewType } from '../../shared/enums/calendar-view-type.enum';
import { debounceTime, distinctUntilChanged, filter, tap } from 'rxjs/operators';
import { BaseStorageService } from '@core/services/base-storage.service';
import { ProjectsTimelineFilterComponent } from './components/projects-timeline-filter/projects-timeline-filter.component';
import { CalendarOptions } from '@fullcalendar/core';
import { ResourceApi } from '@fullcalendar/resource';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ProjectsTimelineSidenavComponent } from '@modules/projects/shared/components/projects-timeline-sidenav/projects-timeline-sidenav.component';
import { getEventTab } from '@modules/projects/shared/helpers/timeline-fns.helper';

@Component({
  selector: 'projects-timeline',
  templateUrl: './projects-timeline.component.html',
  styleUrls: ['./projects-timeline.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProjectsTimelineComponent implements OnInit, AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);

  calendarOptions: CalendarOptions = projectsTimelineCalendarConfig(
    this.languageService,
    this.getResourceLabelComponent.bind(this),
    this.onEventChange.bind(this),
    this.onEventClick.bind(this),
    this.eventDidMount.bind(this),
    this.projectsTimelineStorage,
    this.slotLabelClick.bind(this),
    this.getProjectsFilterComponent.bind(this)
  );
  projectsFilter;
  projectStagesFilter = JSON.parse(this.storage.get('projectStagesFilter')) || PROJECT_STAGES_FILTER;

  sub: Subscription = new Subscription();
  eventController: ProjectsTimelineEventController;
  resourceController: ProjectsTimelineResourceController;
  blockEventUpdate: boolean = false;
  activeProject: Partial<Project>;

  @ViewChild('fullCalendarComponent') fullCalendarComponent: FullCalendarComponent;
  @ViewChild('timeline') timeline: ElementRef;
  @ViewChild('calendarContainer') calendarContainer: ElementRef;
  @ViewChild('timelineHeaderComponent') timelineHeaderComponent: ProjectsTimelineHeaderComponent;

  get calendar() {
    return this.fullCalendarComponent?.getApi();
  }

  constructor(
    private t: TranslateService,
    public n: NavigateService,
    private changes: ChangeDetectorRef,
    private languageService: LanguageService,
    private componentFactoryResolver: ComponentFactoryResolver,
    private appRef: ApplicationRef,
    private injector: Injector,
    private projectStageApiService: ProjectStageAPIService,
    private projectApiService: ProjectAPIService,
    private s: SnackBarService,
    public renderer: Renderer2,
    public viewContainerRef: ViewContainerRef,
    public projectsTimelineStorage: ProjectsTimelineStorageService,
    private sidenav: SidenavService,
    private storage: BaseStorageService
  ) {}

  ngOnInit(): void {
    this.getStages();
    this.handleSubscriptions();
  }

  ngAfterViewInit() {
    this.eventController = new ProjectsTimelineEventController(this.calendar);
    this.resourceController = new ProjectsTimelineResourceController(this.calendar);
    this.setContainersHeight();
    this.setInitialView();
  }

  private getProjectsFilterComponent() {
    if (this.projectsFilter) {
      return {
        domNodes: [this.projectsFilter]
      };
    }

    this.projectsFilter = document.createElement('div');
    const container = this.getDomPortalHost(this.projectsFilter);

    const reference = container.attach(new ComponentPortal(ProjectsTimelineFilterComponent));

    reference.instance.filter.setValue(this.projectStagesFilter);

    reference.instance.onFilterChange
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        tap((stages: string[]) => {
          this.projectStagesFilter = stages;
          this.storage.put('projectStagesFilter', JSON.stringify(this.projectStagesFilter));
          this.updateStagesInStorage();
          this.getStages();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    return {
      domNodes: [this.projectsFilter]
    };
  }

  updateStagesInStorage(): void {
    const rawState = this.storage.get('savedResourcesState');
    let savedResourcesState;

    try {
      savedResourcesState = rawState ? JSON.parse(rawState) : {};
    } catch (e) {
      savedResourcesState = {};
    }

    for (const stageKey of Object.keys(savedResourcesState)) {
      if (Array.isArray(this.projectStagesFilter) && !this.projectStagesFilter.includes(stageKey)) {
        delete savedResourcesState[stageKey];
      }
    }

    this.storage.put('savedResourcesState', JSON.stringify(savedResourcesState));
  }

  private handleSubscriptions(): void {
    this.sidenav.emitter
      .pipe(
        filter(({ type }) => type === SidenavEventType.CLOSE),
        tap(({ data }) => {
          console.log('data', data);
          const project = data ?? (this.activeProject as Project);
          if (!project) return;
          this.onProjectChange({ project });
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  setInitialView(): void {
    this.timelineHeaderComponent.onChangeViewType(this.projectsTimelineStorage.TimelineView);
    this.changes.detectChanges();
  }

  slotLabelClick(e: { level: number; time: Duration; date: Date; view: ViewApi; text: string }) {
    if (e.view.type === 'customThreeYearTimeline' && e.level === 1) {
      this.timelineHeaderComponent.onChangeViewType(CalendarViewType.resourceTimelineMonth);
    }

    if (e.view.type === 'resourceTimelineWeeksSlot') {
      this.timelineHeaderComponent.onChangeViewType(CalendarViewType.resourceTimelineMonth);
    }

    if (e.view.type === 'resourceTimelineMonth' && e.level === 1) {
      this.timelineHeaderComponent.onChangeViewType(CalendarViewType.resourceTimelineWeek);
    }

    if (e.view.type === 'resourceTimelineThreeMonths' && e.level === 1) {
      this.timelineHeaderComponent.onChangeViewType(CalendarViewType.resourceTimelineThreeMonths);
    }

    if (e.view.type === 'resourceTimelineThreeWeeks' && e.level === 1) {
      this.timelineHeaderComponent.onChangeViewType(CalendarViewType.resourceTimelineThreeWeeks);
    }

    this.calendar.gotoDate(e.date);
    this.changes.detectChanges();
  }

  private getStages() {
    this.projectStageApiService
      .getStages({
        'stages[]': this.projectStagesFilter.length ? this.projectStagesFilter : null
      })
      .subscribe({
        next: this.onGetStagesSuccess.bind(this),
        error: () => this.s.error(this.t.instant('ProjectsTimeline.errorGetStages'))
      });
  }

  private onGetStagesSuccess(stages: IProjectStage[]) {
    this.calendar?.removeAllEvents();
    this.resourceController.removeAllResources();
    this.resourceController.addStagesResources(stages);
    this.eventController.addStagesEvents(stages);

    // Get open resources state and open them based on flag
    const savedResourcesStateRaw = this.storage.get('savedResourcesState');
    const savedResourcesState = savedResourcesStateRaw ? JSON.parse(savedResourcesStateRaw) : {};

    Object.values(savedResourcesState).forEach((resource: IProjectsTimelineResourceEvent) => {
      const activeResource = {
        ...resource,
        resource: this.calendar.getResourceById(resource?.resource?.id)
      };

      if (resource.open) this.onResourceToggle(activeResource);
    });
  }

  private getResourceLabelComponent(content: { resource: ResourceApi; fieldValue: string }) {
    const resourceLabel = document.createElement('span');
    let container = this.getDomPortalHost(resourceLabel);

    const reference = container.attach(new ComponentPortal(ProjectsTimelineResourceLabelComponent));
    reference.instance.resource = content.resource;
    reference.instance.eventEmitter
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(this.onResourceToggle.bind(this));
    reference.instance.onProjectChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(this.onProjectChange.bind(this));

    return {
      domNodes: [resourceLabel]
    };
  }

  private getDomPortalHost(element: Element) {
    return new DomPortalHost(element, this.componentFactoryResolver, this.appRef, this.injector);
  }

  private onResourceToggle(e: IProjectsTimelineResourceEvent) {
    const oldScroll = document.querySelector('.fc-timeline-body').parentElement.scrollTop;

    // Save open resources state to local storage
    const savedResourcesState = JSON.parse(this.storage.get('savedResourcesState'));
    this.storage.put('savedResourcesState', JSON.stringify({ ...savedResourcesState, [e.stage]: e }));

    if (e.open) {
      this.getProjectsForStage(e.stage, e.resource).add(() => {
        this.setScrollAfterGetProjects(oldScroll);
      });
    } else {
      e.resource.setExtendedProp('opened', false);
      this.resourceController.removeProjectsResourcesFromStage(e.stage);
      this.eventController.removeEventsWithoutResources();
      this.setScrollAfterGetProjects(oldScroll);
    }
  }

  private getProjectsForStage(stage: ProjectStage, resource: ResourceApi) {
    return this.projectApiService.getProjectsForStage(stage).subscribe({
      next: (projects: Project[]) => {
        this.resourceController.addProjectsResources(projects);
        this.eventController.addProjectsEvents(projects);
        if (resource?._resource?.extendedProps) {
          resource.setExtendedProp('opened', !resource._resource?.extendedProps.opened);
        }
      }
    });
  }

  private setScrollAfterGetProjects(oldScroll) {
    document.querySelector('.fc-timeline-body').parentElement.scrollTop = oldScroll;
    document.querySelector('.fc-datagrid-body').parentElement.scrollTop = oldScroll;
  }

  private onEventClick(e: { el: Element; event: EventApi; view: ViewApi; jsEvent: MouseEvent }) {
    if (e.event.extendedProps.isStageEvent) {
      this.onStageEventClick(e);
    } else {
      this.onProjectEventClick(e);
    }
  }

  private onStageEventClick(e: { el: Element; event: EventApi; view: ViewApi; jsEvent: MouseEvent }) {
    const stageResource = this.calendar.getResourceById(e.event.id);
    this.onResourceToggle({
      open: !stageResource.extendedProps.opened,
      stage: e.event.id as ProjectStage,
      resource: stageResource
    });
  }

  private onProjectEventClick({
    event
  }: {
    el: Element;
    event: EventApi;
    view: ViewApi;
    jsEvent: MouseEvent;
  }): void {
    const project = event.extendedProps;
    const elementId = event.id;
    const tab = getEventTab(event, project?.id);

    this.sidenav.open(
      ProjectsTimelineSidenavComponent,
      { maxWidth: '600px', hideBackdrop: true, hardReload: true },
      { project, tab, elementId }
    );
  }

  measureText(pText: string, pFontSize: number) {
    let lDiv: any = document.createElement('div');

    document.body.appendChild(lDiv);
    lDiv.style.fontSize = '' + pFontSize + 'px';
    lDiv.style.position = 'absolute';
    lDiv.style.left = -1000;
    lDiv.style.top = -1000;

    lDiv.innerHTML = pText;

    let lResult = lDiv.clientWidth;

    document.body.removeChild(lDiv);
    lDiv = null;

    return lResult;
  }

  private eventDidMount(e: {
    el: HTMLElement;
    event: EventApi;
    isDragging?: boolean;
    oldEvent?: EventApi;
    isResizing?: boolean;
  }) {
    // Add overlay when event is resizing or drawing to properly show arrows pointing to start/end of the stage
    if (e.isResizing || e.isDragging) {
      if (!e.el.querySelector('.event-overlay')) {
        const overlay = document.createElement('div');
        overlay.classList.add('event-overlay');
        e.el.appendChild(overlay);
      }
    } else {
      e.el.querySelector('.event-overlay')?.remove();
    }

    // TODO; Finish dragging stages
    // if (!e.isDragging && e.oldEvent && e.event && e.event.start && e.oldEvent.start) {
    //   const delta = e.event.start.getTime() - e.oldEvent.start.getTime();

    //   if (delta !== 0) {
    //     const calendar = this.calendar;

    //     if (calendar) {
    //       const project = e.event.extendedProps;

    //       console.log(project);

    //       const projectFormatted = {
    //         ...project,
    //         basicDataBox: {
    //           ...project.basicDataBox,
    //           activeTimelineStages: project.basicDataBox.activeTimelineStages.map((stage) => {
    //             return {
    //               ...stage,
    //               startDate: new Date(new Date(stage.startDate).getTime() + delta),
    //               endDate: new Date(new Date(stage.endDate).getTime() + delta)
    //             };
    //           })
    //         }
    //       };

    //       console.log(projectFormatted);

    //       this.eventController.updateProjectEvent(projectFormatted as any);
    //     }
    //   }
    // }

    // Open sidenav when event dragging ends to add term change comment
    if (!e.isDragging && e.oldEvent) {
      console.log('mount after dragging');
      this.blockEventUpdate = true;
      const projectBase = e.oldEvent.extendedProps;
      this.activeProject = projectBase;
      const start = moment(e.event.start).format(Config.DATE_SERVER);
      let end = moment(e.event.end);

      if (e.event.allDay || end.isSame(end.clone().startOf('day'))) end = end.subtract(1, 'day');

      const formattedEnd = end.format(Config.DATE_SERVER);

      const elementId = e.event.id;

      const tab = getEventTab(e.event, projectBase.id);

      const project = {
        ...projectBase,
        basicDataBox: {
          ...projectBase.basicDataBox,
          ...(tab === 'range'
            ? { termStart: start, termEnd: formattedEnd }
            : tab === 'pause'
              ? { pauseStart: start, pauseEnd: formattedEnd }
              : {
                  activeTimelineStages: projectBase?.basicDataBox?.activeTimelineStages?.map((stage) =>
                    `stage-${stage?.timelineStage?.id?.toString()}` === e.event.id
                      ? { ...stage, startDate: start, endDate: formattedEnd }
                      : stage
                  )
                })
        }
      };

      this.sidenav.open(
        ProjectsTimelineSidenavComponent,
        { maxWidth: '600px', hideBackdrop: true, hardReload: true },
        { project, tab, elementId, forceComment: true }
      );
    }

    const colorProp = e.event.extendedProps['color'];

    if (colorProp) e.el.style.setProperty('--event-color', colorProp);

    const pos = e.el.getBoundingClientRect();
    const scroller = document.querySelector('.fc-timeline-body').parentNode as HTMLElement;
    const textContent = e.el.querySelector('.fc-event-title').textContent;

    if (scroller) {
      if (scroller.clientWidth !== scroller.scrollWidth) {
        const contentWidth = this.measureText(textContent, 11);
        if (
          pos.right > scroller.clientWidth ||
          pos.right < 0 ||
          (pos.width < contentWidth && pos.right + contentWidth - 200 > scroller.clientWidth)
        ) {
          e.el.classList.add('rtl');
        }
      }
    }
  }

  private onEventChange(e: { event: EventApi; oldEvent: EventApi; relatedEvents?; revert?: Function }) {
    //if it's edit event for whole stage not event connected with project
    if (!e.event?.extendedProps?.id || this.blockEventUpdate) {
      return;
    }

    console.log('onEventChange');

    this.blockEventUpdate = true;
    const project = new Project(e.event.extendedProps);
    let newStage = project.stage;
    const startDate = this.getChangeEventNewDate('start', e);
    const endDate = this.getChangeEventNewDate('end', e);
    project.basicDataBox.termStart = startDate;
    project.basicDataBox.termEnd = endDate;
    e.event.extendedProps.basicDataBox.termStart = startDate;
    e.event.extendedProps.basicDataBox.termEnd = endDate;
    e.event.setProp('classNames', 'fc-event--' + newStage + ' fc-event--project-event');

    if ((e.event as any)?.resourceIds[0] !== project.id.toString()) {
      newStage = (e.event as any).resourceIds[0] as ProjectStage;
      if (newStage === ProjectStage.STAGE_LOST) {
        this.setLostStage(project, newStage, startDate, endDate, e);
        return false;
      }
      this.resourceController.updateResourcesAfterEventChange(project, newStage, e.event);
    } else {
      this.eventController.updateOpenedStageEvent(newStage);
    }
    this.resourceController.updateProjectResource(project);
    const ctrl = new ProjectController(project);
    ctrl.setStage(project, newStage, startDate, endDate).toPromise();
    this.blockEventUpdate = false;

    this.changes.detectChanges();
  }

  onProjectChange(e: { project: Project; oldStage?: ProjectStage }) {
    this.blockEventUpdate = true;
    this.activeProject = e.project;
    this.eventController.updateProjectEvent(e.project);
    this.resourceController.updateProjectResource(e.project);
    const newStage = e.project.stage;
    e.oldStage ? (e.project.stage = e.oldStage) : '';
    e.oldStage ? this.resourceController.updateResourcesAfterEventChange(e.project, newStage, null) : '';
    this.blockEventUpdate = false;
    this.changes.detectChanges();
  }

  private setLostStage(
    project: Project,
    newStage: ProjectStage,
    startDate: string,
    endDate: string,
    e: { event: EventApi; revert?: Function }
  ) {
    const ctrl = new ProjectController(project);
    ctrl
      .setStage(project, newStage, startDate, endDate)
      .subscribe((projectChangeStageEvent: ProjectChangeStageEvent) => {
        switch (projectChangeStageEvent.type) {
          case ProjectChangeStageEventType.CANCELED:
            e.revert();
            this.blockEventUpdate = false;
            this.changes.detectChanges();
            break;
          case ProjectChangeStageEventType.CHANGED:
            this.resourceController.updateResourcesAfterEventChange(project, newStage, e.event);
            this.blockEventUpdate = false;
            this.changes.detectChanges();
            break;
        }
      });
    return false;
  }

  private getChangeEventNewDate(type: string, e: { event: EventApi; oldEvent: EventApi; relatedEvents? }) {
    return e.event[type]
      ? moment(e.event[type]).format(Config.DATE_SERVER)
      : moment(e.oldEvent[type]).format(Config.DATE_SERVER);
  }

  private setContainersHeight() {
    if (WindowHelper.isMobileWidth) {
      let clientHeight = document.documentElement.clientHeight;
      this.timeline.nativeElement.style.height = clientHeight - 65 + 'px';
      this.calendarContainer.nativeElement.style.height = clientHeight - 65 - 55 + 'px';
    }
  }

  @HostListener('window:resize')
  @debounce(100)
  windowResize() {
    this.setContainersHeight();
  }
}
